import {
  ACCOUNTING,
  ADMIN,
  AUDITOR,
  FINANCE,
  SALARY_BILLING_HR,
} from "../user/accessRightChecks";
import { ClosingMessageLanguage } from "./conversationClosingMessage";
import {
  InboxTabId,
  isTeamOpenConversation,
  matchesInboxTab,
} from "./inboxTabs";
import {
  ConversationTransferDestination,
  TransferConversation,
} from "./types/commands";
import { ConversationStatus } from "./types/queries";

export type TransferConversationOptions = {
  destination: ConversationTransferDestination;
  sendTransferMessage: boolean;
  language: ClosingMessageLanguage;
};

export type TransferMessageKind = "team" | "expert";

type TransferConversationState = {
  status: ConversationStatus;
  assigneeId?: string | null;
  teamIds?: string[];
};

export type TransferTeamChoice = {
  key: string;
  label: string;
  teamIds: string[];
  disabled: boolean;
};

export const conversationChangesAfterTransfer = (
  destination: ConversationTransferDestination,
) => ({
  status: ConversationStatus.OPEN,
  assigneeId: null,
  teamIds: destination.type === "TEAM" ? destination.ids : [],
});

export const remainsInInboxAfterTransfer = ({
  destination,
  tabId,
  userId,
  initiatorId,
  myTeamIds = [],
}: {
  destination: ConversationTransferDestination;
  tabId: InboxTabId;
  userId?: string;
  initiatorId?: string;
  myTeamIds?: string[];
}): boolean => {
  const next = conversationChangesAfterTransfer(destination);
  if (!matchesInboxTab(next, tabId)) return false;
  if (destination.type === "TEAM") {
    return destination.ids.some((id) => myTeamIds.includes(id));
  }
  const recipientId = destination.ids[0];
  return recipientId === userId || initiatorId === userId;
};

export const memberTeamIds = (
  teams: { id: string; memberIds: string[] }[],
  userId?: string,
): string[] => {
  if (!userId) return [];
  return teams
    .filter((team) => team.memberIds.includes(userId))
    .map((team) => team.id);
};

export const transferExcludedTeamId = (
  conversation: TransferConversationState | null | undefined,
): string | undefined => {
  if (!conversation || !isTeamOpenConversation(conversation)) return undefined;
  return conversation.teamIds?.length === 1
    ? conversation.teamIds[0]
    : undefined;
};

// Not a team id. This row sends every team the user belongs to.
export const OWN_TEAM_CHOICE_KEY = "own";

// ownTeamLabel is already translated. SUPPORT is omitted; a member reaches it through Own team.
export const buildTransferTeamChoices = ({
  teams,
  myTeamIds,
  ownTeamEnabled,
  ownTeamLabel,
  excludedTeamId,
}: {
  teams: { id: string; name: string; type?: string }[];
  myTeamIds: string[];
  ownTeamEnabled: boolean;
  ownTeamLabel: string;
  excludedTeamId?: string;
}): TransferTeamChoice[] => {
  const duplicateOwnTeamId = myTeamIds.length === 1 ? myTeamIds[0] : undefined;
  const named = teams
    .filter(
      (team) =>
        team.type !== "SUPPORT" &&
        team.id !== duplicateOwnTeamId &&
        team.id !== excludedTeamId,
    )
    .map((team) => ({
      key: team.id,
      label: team.name,
      teamIds: [team.id],
      disabled: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "fi"));

  return [
    {
      key: OWN_TEAM_CHOICE_KEY,
      label: ownTeamLabel,
      teamIds: myTeamIds,
      disabled: !ownTeamEnabled || myTeamIds.length === 0,
    },
    ...named,
  ];
};

export const canTransferConversation = ({
  status,
  assigneeId,
  userId,
  hasMessages,
}: {
  status?: ConversationStatus;
  assigneeId?: string | null;
  userId?: string;
  hasMessages: boolean;
}): boolean => {
  if (!hasMessages || !userId) return false;
  if (
    status !== ConversationStatus.OPEN &&
    status !== ConversationStatus.IN_PROGRESS
  ) {
    return false;
  }
  return !assigneeId || assigneeId === userId;
};

const EXCLUDED_TRANSFER_SCOPES = [
  ADMIN,
  SALARY_BILLING_HR,
  FINANCE,
  ACCOUNTING,
  AUDITOR,
];

export const canReceiveTransferredConversation = (expert: {
  account: { enabled?: boolean; authScopes: string[] };
}) =>
  expert.account.enabled !== false &&
  !expert.account.authScopes.some((scope) =>
    EXCLUDED_TRANSFER_SCOPES.includes(scope),
  );

export const canTransferToTeam = (
  conversation: TransferConversationState | null | undefined,
  teamIds: string[],
): boolean =>
  teamIds.length > 0 && !!conversation && !isTeamOpenConversation(conversation);

export const TRANSFER_MESSAGES: Record<
  TransferMessageKind,
  Record<ClosingMessageLanguage, string>
> = {
  team: {
    fi: "Keskustelusi on siirretty tiimin käsiteltäväksi. Seuraava vapaa asiantuntija ottaa asian käsittelyyn mahdollisimman pian.",
    en: "Your conversation has been moved to the team queue. The next available expert will take it from there as soon as possible.",
  },
  expert: {
    fi: "Keskustelusi on siirretty toiselle asiantuntijalle, jotta asia voidaan käsitellä mahdollisimman sujuvasti.",
    en: "Your conversation has been transferred to another expert so it can be handled as smoothly as possible.",
  },
};

export const getTransferMessage = (
  kind: TransferMessageKind,
  language: ClosingMessageLanguage,
): string => TRANSFER_MESSAGES[kind][language] ?? TRANSFER_MESSAGES[kind].fi;

export const transferrerDisplayName = (
  expert:
    | { account?: { firstName?: string | null; lastName?: string | null } }
    | null
    | undefined,
): string | null => {
  const firstName = expert?.account?.firstName?.trim();
  const lastName = expert?.account?.lastName?.trim();
  if (!firstName || !lastName) return null;
  return `${firstName} ${lastName}`;
};

export const getTransferMessageToSend = ({
  sendTransferMessage,
  language,
  kind,
}: {
  sendTransferMessage: boolean;
  language: ClosingMessageLanguage;
  kind: TransferMessageKind;
}): string | null => {
  if (!sendTransferMessage) return null;
  return getTransferMessage(kind, language);
};

export const toTransferConversationCommand = (
  conversationId: string,
  { destination, sendTransferMessage, language }: TransferConversationOptions,
): TransferConversation => {
  const notice = getTransferMessageToSend({
    sendTransferMessage,
    language,
    kind: destination.type === "TEAM" ? "team" : "expert",
  });
  return {
    conversationId,
    destination,
    ...(notice ? { notice } : {}),
  };
};
